<template>
  <div>
    <!-- Collection Modal -->
    <modal id="open_to_add_collection_name" :adaptive="false" name="open_to_add_collection_name"
           style="z-index:999999;">
      <AddCollectionModal ref="addCollectionModal" @onSubmit="addCollectionModalSubmit"/>
    </modal>

    <!-- Collection Panel -->
    <div v-show="visible" class="ad-collection-panel">
      <a class="ad-collection-close" @click="$emit('close')"><i class="uil-times"></i></a>
      <div class="ad-collection-header">
        <i class="uil-notebooks"></i>
        <h5>{{ $t("education.Add_collection") }}</h5>
      </div>
      <p>{{ $t("education.Add_collection_text") }}</p>
      <MultipleSelectInput :model.sync="selectedCollection"
                           :searchable="true"
                           :show-labels="false"
                           :values="collectionOptions"
                           label="name"
                           track-by="id"
                           @select="onCollectionSelect"/>
      <div class="ad-collection-actions">
        <button :disabled="contentIds.length === 0 || !collectionId"
                class="ad-btn ad-btn--primary ad-btn--sm" type="button" @click.prevent="submit">
          <i class="uil-notebooks"></i>
          <span>{{ $t("general.save") }}</span>
        </button>
        <button class="ad-btn ad-btn--ghost ad-btn--sm" type="button" @click="$emit('close')">
          {{ $t("general.give_up") }}
        </button>
      </div>
    </div>

    <!-- Collection Checkbox List -->
    <ul class="course-curriculum" uk-accordion="multiple: true">
      <li v-if="visible" class="uk-open">
        <a class="uk-accordion-title" href="#">{{ $t("education.collection_subDiv") }}</a>
        <div class="uk-accordion-content">
          <ul class="course-curriculum-list">
            <li v-for="(education, index) in educations" :key="'add_collection_' + index" class="watch">
              <input :id="'add_collection_checkbox_' + index" v-model="contentIds"
                     :value="education.id" class="d-inline-flex mb-0 mr-1" type="checkbox"/>
              <a>
                <label :for="'add_collection_checkbox_' + index" class="small m-0 cursor-pointer">
                  {{ education.title }}</label>
              </a>
              <span><i class="uil-check-circle"></i>{{ education.point }} {{ $t("point.point") }}</span>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import AddCollectionModal from "@/view/components/educationDetail/AddCollectionModal.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import { CREATE_ITEM, ERROR as REST_ERROR, GET_ITEMS } from "@/core/services/store/REST.module";

const DEFAULT_COLLECTION_CONTENT_URL = 'api/assignment/collection-content/';

export default {
  name: "AssignmentCollection",
  components: { AddCollectionModal, MultipleSelectInput },
  props: {
    visible: { type: Boolean, default: false },
    assignmentId: { type: [String, Number], required: true },
    educations: { type: Array, default: () => [] },
    collectionContentUrl: { type: String, default: DEFAULT_COLLECTION_CONTENT_URL },
  },
  emits: ['close', 'success', 'error'],
  data() {
    return {
      collections: [],
      selectedCollection: null,
      contentIds: [],
    };
  },
  computed: {
    collectionVisible() {
      const modules = JSON.parse(window.localStorage.getItem("modules")) ?? [];
      return modules.includes("my-collection");
    },
    restError() {
      return this.$store.getters[REST_ERROR];
    },
    collectionId() {
      return this.selectedCollection?.id ?? null;
    },
    collectionOptions() {
      const newOption = { id: 0, name: '[+] ' + this.$t("education.New_Collection") };
      return [newOption, ...this.collections];
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.contentIds = [];
        this.selectedCollection = null;
      }
    },
  },
  mounted() {
    this.getCollectionItems();
  },
  methods: {
    onCollectionSelect(value) {
      if (value && +value.id === 0) {
        this.$modal.show("open_to_add_collection_name");
        this.$nextTick(() => {
          this.selectedCollection = null;
        });
      }
    },
    getCollectionItems() {
      if (!this.collectionVisible) return;

      this.$store
          .dispatch(GET_ITEMS, { url: 'api/collection', acceptPromise: true })
          .then((result) => {
            if (result.status) this.collections = result.data.my_collections;
          });
    },
    addCollectionModalSubmit(payload) {
      if (!this.collectionVisible) return;
      this.$refs["addCollectionModal"].closeModal();

      if (payload.success) {
        this.$emit('success', this.$t("education.collection_name_success"));
        const newCol = { id: payload.data.id, name: payload.data.name };
        this.collections = [...this.collections, newCol];
        this.selectedCollection = newCol;
      } else {
        this.$emit('error', payload.message);
      }
    },
    submit() {
      if (!this.collectionId) {
        this.$emit('error', this.$t("education.please_add_collection_name"));
        return;
      }
      if (!this.contentIds.length) {
        this.$emit('error', this.$t("education.please_select_contents"));
        return;
      }

      this.$store
          .dispatch(CREATE_ITEM, {
            url: this.collectionContentUrl + this.assignmentId,
            contents: {
              content_ids: this.contentIds,
              collection_id: this.collectionId,
            },
          })
          .then(() => {
            if (!this.restError) {
              this.$emit('success', this.$t("education.collection_created"));
            }
            this.$emit('close');
          });
    },
  },
};
</script>

<style>
/* ── Button base ── */
.ad-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.ad-btn--primary {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
}

.ad-btn--primary:hover {
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
  color: #fff;
}

.ad-btn--ghost {
  background: transparent;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.ad-btn--ghost:hover {
  background: #f8fafc;
  color: #475569;
}

.ad-btn--sm {
  padding: 8px 16px;
  font-size: 13px;
}

/* ── Collection Panel ── */
.ad-collection-panel {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  position: relative;
}

.ad-collection-close {
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  color: #64748b;
  font-size: 18px;
}

.ad-collection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ad-collection-header i {
  font-size: 20px;
  color: #2563eb;
}

.ad-collection-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.ad-collection-panel p {
  color: #475569;
  font-size: 14px;
  margin-bottom: 12px;
}

.ad-collection-actions {
  display: flex;
  gap: 8px;
}

/* ── Mobile ── */
@media (max-width: 960px) {
  .ad-btn {
    padding: 10px 14px;
    font-size: 13px;
    border-radius: 8px;
  }

  .ad-btn i {
    font-size: 15px;
  }

  .ad-btn span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ad-collection-panel {
    padding: 16px;
  }

  .ad-collection-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .ad-btn {
    padding: 10px 12px;
    font-size: 12px;
    border-radius: 8px;
  }
}
</style>
